import { NEUTRAL_COLOR_IDS } from '../catalog/colors';
import type { ImageAdjustments } from '../import/adjust';
import type { QuantizationMode } from '../pipeline/pipeline';
import type { ResamplingMode } from '../pipeline/resize';

/**
 * Presets de style (Lot P3 — CDC §4.4 étape 3).
 *
 * Règle produit cardinale : AUCUN nom d'algorithme dans l'UI publique.
 * Chaque preset pilote en coulisses resampling / tramage / plafond couleurs /
 * réglages image. Le seul curseur exposé est « Détails » (0-100), qui module
 * l'intensité du tramage autour de la valeur par défaut du preset.
 */
export type StyleKey = 'fidele' | 'douce' | 'pop' | 'nb';

export type StylePreset = {
  key: StyleKey;
  label: string;
  desc: string;
  pipeline: {
    resampling: ResamplingMode;
    quantization: QuantizationMode;
    /** Plafond de couleurs (= sachets du kit). */
    maxColors: number;
    adjustments?: ImageAdjustments;
    /** Restriction dure de palette (ex. N&B → neutres uniquement). */
    allowedColorIds?: ReadonlyArray<string>;
  };
  /** Position par défaut du slider « Détails » (0-100 → ditherStrength). */
  defaultDetail: number;
};

export const STYLE_PRESETS: ReadonlyArray<StylePreset> = [
  {
    key: 'fidele',
    label: 'Fidèle',
    desc: 'Le plus proche de ta photo, nuances douces',
    pipeline: { resampling: 'bilinear', quantization: 'dither', maxColors: 25 },
    defaultDetail: 60,
  },
  {
    key: 'douce',
    label: 'Douce',
    desc: 'Aplats lissés, rendu apaisé',
    pipeline: { resampling: 'bilinear', quantization: 'dither', maxColors: 16 },
    defaultDetail: 15,
  },
  {
    key: 'pop',
    label: 'Pop',
    desc: 'Couleurs franches, esprit BD',
    pipeline: {
      resampling: 'bilinear',
      quantization: 'dither',
      maxColors: 10,
      adjustments: { contrast: 0.25, saturation: 0.3 },
    },
    defaultDetail: 0,
  },
  {
    key: 'nb',
    label: 'Noir & blanc',
    desc: 'Intemporel, contrastes profonds',
    pipeline: {
      resampling: 'bilinear',
      quantization: 'dither',
      maxColors: 8,
      adjustments: { saturation: -1, contrast: 0.15 },
      // Garantie N&B : palette restreinte aux neutres, sinon nearest/dither
      // pose des perles colorées pour approcher un gris (revue V2).
      allowedColorIds: NEUTRAL_COLOR_IDS,
    },
    defaultDetail: 40,
  },
];

export function stylePreset(key: StyleKey): StylePreset {
  const p = STYLE_PRESETS.find((s) => s.key === key);
  if (!p) throw new Error(`Style inconnu : ${key}`);
  return p;
}

/**
 * Défauts « Détails » (tramage) et « Couleurs » par (style × taille).
 *
 * Fondement (benchmark scripts/bench/ + revue métrologique 07/2026) —
 * ce qui est MESURÉ et solide :
 *  - le tramage fort détruit les petites grilles (écart 15+ pts de composite
 *    à 29×29, très au-delà du bruit) → détail ≈ 0 en XS est un résultat ;
 *  - resampling bilinear > nearest, à toutes les tailles.
 * Ce qui N'EST PAS tranché par le bench (les deux métriques près/recul se
 * contredisent à TOUTES les tailles ; corpus picsum sans portraits ; écarts
 * de plafond couleurs dans le bruit) :
 *  - la rampe de tramage croissante avec la taille est une HYPOTHÈSE PRODUIT
 *    (les dégradés des grands formats en profitent esthétiquement — l'œil
 *    intègre la texture) en attente d'arbitrage HUMAIN sur rendus à taille
 *    réelle (scripts/bench/gallery.ts). À 1-2 m, une perle de 5 mm reste
 *    optiquement résoluble — le tramage est un choix de texture, pas une
 *    fusion optique. Le montage se faisant de près, la rampe reste modérée.
 * Le client garde les deux sliders pour dévier de ces défauts.
 */
export type SizeTuning = { detail: number; colors: number };

/** Classes de taille (plus grand côté en perles) : XS <45, M <75, L <100, XL ≥100. */
function sizeClassIndex(maxSide: number): 0 | 1 | 2 | 3 {
  if (maxSide < 45) return 0;
  if (maxSide < 75) return 1;
  if (maxSide < 100) return 2;
  return 3;
}

const SIZE_TUNING: Record<StyleKey, readonly [SizeTuning, SizeTuning, SizeTuning, SizeTuning]> = {
  //        XS (≈29)              M (≈58)               L (≈87)               XL (≥116, fresques)
  fidele: [
    { detail: 0, colors: 12 },
    { detail: 15, colors: 16 },
    { detail: 30, colors: 20 },
    { detail: 40, colors: 25 },
  ],
  douce: [
    { detail: 0, colors: 8 },
    { detail: 10, colors: 10 },
    { detail: 15, colors: 12 },
    { detail: 25, colors: 16 },
  ],
  pop: [
    { detail: 0, colors: 8 },
    { detail: 0, colors: 9 },
    { detail: 0, colors: 10 },
    { detail: 0, colors: 10 },
  ],
  // N&B : palette restreinte à 4 neutres (cf. NEUTRAL_COLOR_IDS) → couleurs
  // plafonnées à 4 ; le tramage lisse les dégradés entre ces 4 tons.
  nb: [
    { detail: 10, colors: 3 },
    { detail: 25, colors: 4 },
    { detail: 45, colors: 4 },
    { detail: 60, colors: 4 },
  ],
};

/** Réglages par défaut pour un style et un plus grand côté de grille. */
export function defaultTuningFor(style: StyleKey, maxSide: number): SizeTuning {
  const side = Number.isFinite(maxSide) && maxSide > 0 ? maxSide : 58;
  return SIZE_TUNING[style][sizeClassIndex(side)];
}

/**
 * Seuil de fusion des couleurs rares, proportionnel à la taille du patron
 * (politique kit : pas de sachet quasi vide). Min 8 perles, ~0,3 % du total.
 */
export function mergeThresholdFor(totalBeads: number): number {
  return Math.max(8, Math.round(totalBeads * 0.003));
}

/** Slider « Détails » (0-100) → intensité de tramage (0-1). */
export function detailToStrength(detail: number): number {
  if (!Number.isFinite(detail)) return 0.6;
  return Math.max(0, Math.min(100, detail)) / 100;
}
